import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonGroup, Col, Form, Row } from 'react-bootstrap';
import { AlertTriangle } from 'react-feather';
import { toast } from 'react-toastify';
import { acknowledgeAlertAPI, getAlertsListAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import StatusBadge from '../../../Components/shared/StatusBadge';
import DrawerPanel from '../../../Components/shared/DrawerPanel';
import { platformUrl } from '../../../utils/mhNav';
import styles from './AlertCenter.module.scss';

const SEV_ORDER = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };

function sortAlerts(rows) {
  return [...rows].sort((a, b) => {
    const ra = SEV_ORDER[String(a.severity).toLowerCase()] ?? 0;
    const rb = SEV_ORDER[String(b.severity).toLowerCase()] ?? 0;
    if (rb !== ra) return rb - ra;
    return new Date(b.time) - new Date(a.time);
  });
}

export default function AlertCenter() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [severity, setSeverity] = useState('all');
  const [subsystem, setSubsystem] = useState('all');
  const [search, setSearch] = useState('');
  const [drawerRow, setDrawerRow] = useState(null);
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAlertsListAPI();
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let r = rows;
    if (severity !== 'all') {
      r = r.filter((x) => String(x.severity).toLowerCase() === severity);
    }
    if (subsystem !== 'all') {
      r = r.filter((x) =>
        String(x.subsystem || x.source || '')
          .toLowerCase()
          .includes(subsystem.toLowerCase())
      );
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) => String(x.message).toLowerCase().includes(q));
    }
    return sortAlerts(r);
  }, [rows, severity, subsystem, search]);

  const handleAck = async (id) => {
    try {
      await acknowledgeAlertAPI(id);
      toast.success('Alert acknowledged');
      await load();
    } catch (e) {
      toast.error(e.message || 'Ack failed');
    }
  };

  const bulkAck = async () => {
    for (const row of selected) {
      await acknowledgeAlertAPI(row.id);
    }
    toast.success(`Acknowledged ${selected.length} alert(s)`);
    setSelected([]);
    await load();
  };

  const columns = useMemo(
    () => [
      {
        name: 'Time',
        selector: (row) => row.time,
        sortable: false,
        width: '160px',
        cell: (row) => new Date(row.time).toLocaleString(),
      },
      {
        name: 'Severity',
        selector: (row) => row.severity,
        sortable: false,
        width: '120px',
        cell: (row) => <StatusBadge status={row.severity} />,
      },
      {
        name: 'Source',
        selector: (row) => row.source || row.subsystem,
        sortable: false,
        width: '120px',
      },
      {
        name: 'Message',
        selector: (row) => row.message,
        wrap: true,
        sortable: false,
      },
      {
        name: 'Impact',
        selector: (row) => row.impact,
        width: '120px',
        sortable: false,
      },
      {
        name: 'Owner',
        selector: (row) => row.owner,
        width: '140px',
        sortable: false,
      },
      {
        name: 'Action',
        width: '220px',
        cell: (row) => (
          <div className="d-flex flex-wrap gap-1">
            <Button size="sm" variant="outline-primary" disabled={row.acknowledged} onClick={() => handleAck(row.id)}>
              Acknowledge
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => toast.info('Assign (simulated)')}>
              Assign
            </Button>
            <Button size="sm" variant="link" className="p-0 small" onClick={() => toast.info('Mute rule (simulated)')}>
              Mute Rule
            </Button>
            {row.muted && <StatusBadge status="muted" className="ms-1" />}
          </div>
        ),
      },
    ],
    []
  );

  if (loading && !rows.length) {
    return (
      <div>
        <PageHeader
          title="Alert Center"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Dashboard', path: platformUrl('dashboard') },
            { label: 'Alerts' },
          ]}
        />
        <LoadingSkeleton rows={6} cols={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Alert Center"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Dashboard', path: platformUrl('dashboard') },
            { label: 'Alerts' },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load alerts"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Alert Center"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Dashboard', path: platformUrl('dashboard') },
          { label: 'Alerts' },
        ]}
      />

      <div className={`${styles.toolbar} mb-3 p-3 bg-white rounded-3`}>
        <Row className="g-2 align-items-center">
          <Col xs={12} md="auto">
            <span className="small text-muted me-1">Severity</span>
            <ButtonGroup size="sm">
              {['all', 'critical', 'high', 'medium', 'low'].map((s) => (
                <Button
                  key={s}
                  variant={severity === s ? 'primary' : 'outline-secondary'}
                  onClick={() => setSeverity(s)}
                >
                  {s === 'all' ? 'All' : s}
                </Button>
              ))}
            </ButtonGroup>
          </Col>
          <Col xs={12} md="auto">
            <Form.Select size="sm" value={subsystem} onChange={(e) => setSubsystem(e.target.value)} style={{ minWidth: 160 }}>
              <option value="all">All subsystems</option>
              <option value="Microsoft">Microsoft</option>
              <option value="Google">Google</option>
              <option value="WhatsApp">WhatsApp</option>
              <option value="Jobs">Jobs</option>
              <option value="Billing">Billing</option>
            </Form.Select>
          </Col>
          <Col xs={12} md>
            <Form.Control
              size="sm"
              type="search"
              placeholder="Search message…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
        </Row>
      </div>

      {selected.length > 0 && (
        <div className={`${styles.bulkBar} mb-2 p-2 d-flex align-items-center gap-2 flex-wrap`}>
          <span className="small">{selected.length} selected</span>
          <Button size="sm" variant="primary" onClick={bulkAck}>
            Bulk Acknowledge
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => toast.info('Maintenance window (simulated)')}>
            Create Maintenance Window
          </Button>
        </div>
      )}

      <DataTableWrapper
        columns={columns}
        data={filtered}
        loading={false}
        showSearch={false}
        keyField="id"
        selectableRows
        onSelectedRowsChange={({ selectedRows }) => setSelected(selectedRows)}
        onRowClick={(row) => {
          setDrawerRow(row);
          setNotes('');
        }}
        customTableProps={{
          conditionalRowStyles: [
            {
              when: (row) => row.muted,
              style: { opacity: 0.55 },
            },
          ],
        }}
      />

      <DrawerPanel
        isOpen={Boolean(drawerRow)}
        onClose={() => setDrawerRow(null)}
        title={drawerRow ? `Alert ${drawerRow.id}` : ''}
        width={520}
      >
        {drawerRow && (
          <div className="small">
            <p>
              <StatusBadge status={drawerRow.severity} /> <span className="text-muted">{drawerRow.source}</span>
            </p>
            <p>{drawerRow.message}</p>
            <h6 className="mt-3">Timeline</h6>
            <ul>
              <li>{new Date(drawerRow.time).toLocaleString()} — Opened</li>
              <li>{new Date(Date.now() - 600000).toLocaleString()} — Correlated with health checks</li>
            </ul>
            <h6>Impacted tenants</h6>
            <ul>
              <li>MH-0001</li>
              <li>MH-0004</li>
            </ul>
            <Button
              variant="link"
              className="p-0"
              size="sm"
              onClick={() => navigate(platformUrl('support'))}
            >
              Suggested runbook: Webhook storm
            </Button>
            <h6 className="mt-3">Notes</h6>
            <Form.Control
              as="textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal markdown notes…"
            />
            <h6 className="mt-3">Comments</h6>
            <ul className="text-muted">
              <li>oncall1: triaged — watching queue depth</li>
            </ul>
          </div>
        )}
      </DrawerPanel>
    </div>
  );
}
